import React, { useState } from 'react';
import _ from 'lodash';
import { useHydratePricesAndStock } from '../utils/public-api';
import { useGetCartQuery } from '../generated/types-and-hooks';
import log from 'loglevel';
import { UseQueryResult } from '@tanstack/react-query';
import { GetCartQuery } from '../generated/types-and-hooks';

const unitPriceForProduct = (Product, PlantType) => {
  if (!Product) {
    return 0;
  }
  const { Price, PricePlugtrays } = Product;
  switch (PlantType) {
    case 'POT':
      return Price;
    case 'PLUGTRAY':
      return PricePlugtrays;
    case 'COLLECTION':
      return Price;
    default:
      throw new Error(`invalid PlantType ${PlantType}`);
  }
};

type CartContextType = {
  Cart: UseQueryResult<GetCartQuery, unknown>;
  ShowWidget: Function;
};

const Context = React.createContext<CartContextType | undefined>(undefined);
const { Consumer, Provider } = Context;

const CartContext = ({ children }) => {
  const [WidgetVisible, SetWidgetVisible] = useState(false);
  const ShowWidget = () => {
    SetWidgetVisible(true);
    setTimeout(() => {
      SetWidgetVisible(false);
    }, 5000);
  };

  const Cart = useGetCartQuery();
  const ProductIds = _.sortedUniq(
    _.map(_.get(Cart, ['data', 'GetCart', 'Products']), ({ Id }) => Id),
  );
  const CartProducts = useHydratePricesAndStock({
    Products: ProductIds.map((Id) => ({ Id })),
  });
  const productsById = _.keyBy(CartProducts.data?.Products, ({ Id }) => Id);

  const CartProductsHydrated = _.map(
    _.get(Cart, ['data', 'GetCart', 'Products']),
    ({ Id, Quantity, PlantType }) => {
      const Product = _.get(productsById, Id);
      const UnitPrice = unitPriceForProduct(Product, PlantType);
      const LinePrice = UnitPrice * Quantity;
      return { ...Product, Id, Quantity, PlantType, UnitPrice, LinePrice };
    },
  );

  const plugtrayProducts = _.filter(
    CartProductsHydrated,
    ({ PlantType }) => PlantType === 'PLUGTRAY',
  );
  const potProducts = _.filter(
    CartProductsHydrated,
    ({ PlantType }) => PlantType === 'POT',
  );
  const Subtotal = _.sum(
    _.map(CartProductsHydrated, ({ LinePrice }) => LinePrice),
  );
  const PlugtrayCount = _.sum(
    _.map(plugtrayProducts, ({ Quantity }) => Quantity),
  );
  const PotCount = _.sum(_.map(potProducts, ({ Quantity }) => Quantity));
  const GiftCards = Cart.data?.GetCart?.GiftCards;
  return (
    <Provider
      value={{
        Cart,
        CartProducts,
        ShowWidget,
        WidgetVisible,
        SetWidgetVisible,
        Subtotal,
        PlugtrayCount,
        PotCount,
        CartProductsHydrated,
        GiftCards,
      }}
    >
      {children}
    </Provider>
  );
};

export default {
  Consumer,
  Provider: CartContext,
  Context,
};
